import panzoom, { type PanZoom, type Transform } from "panzoom"

// Shared zoom/pan engine for the fullscreen image overlays. Desktop interaction
// (mouse drag + wheel) is handled by panzoom as usual; touch is taken over
// entirely by the gesture layer below, because panzoom's built-in touch handling
// jumps on the 2-finger -> 1-finger transition. Double-tap / double-click zoom is
// disabled on both platforms (custom path removed below; panzoom's own disabled
// via `zoomDoubleClickSpeed: 1`).
//
// We win control of touch by registering our own `touchstart` on the container
// *before* calling `panzoom()`. Listeners on the same target fire in registration
// order, so `stopImmediatePropagation()` here prevents panzoom's touch handler
// from ever starting (and from attaching its own document touchmove/touchend).

const MAX_ZOOM = 5
const MIN_ZOOM = 1
const ZOOM_STEP = 1.5
export const PAN_STEP = 60

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max)

const touchDist = (a: Touch, b: Touch) =>
  Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)

export const useImageZoom = () => {
  let instance: PanZoom | null = null
  let el: HTMLElement | null = null

  // Gesture baseline — re-captured whenever the active finger count changes so a
  // pinch -> pan (or pan -> pinch) transition never produces a positional leap.
  let activeTouches = 0
  let startDist = 0
  let startScale = 1
  let lastMidX = 0
  let lastMidY = 0
  let lastPanX = 0
  let lastPanY = 0

  const captureBaseline = (e: TouchEvent) => {
    if (!instance) return
    startScale = instance.getTransform().scale
    if (e.touches.length >= 2) {
      const a = e.touches[0]
      const b = e.touches[1]
      startDist = touchDist(a, b)
      lastMidX = (a.clientX + b.clientX) / 2
      lastMidY = (a.clientY + b.clientY) / 2
    } else if (e.touches.length === 1) {
      lastPanX = e.touches[0].clientX
      lastPanY = e.touches[0].clientY
    }
  }

  const onTouchStart = (e: TouchEvent) => {
    // Take over touch entirely — panzoom's own touch handler must never run.
    e.stopImmediatePropagation()
    activeTouches = e.touches.length
    captureBaseline(e)
  }

  const onTouchMove = (e: TouchEvent) => {
    if (!instance) return

    // Finger count changed mid-gesture (e.g. lifting one finger of a pinch):
    // re-baseline and wait for the next move so deltas start from here.
    if (e.touches.length !== activeTouches) {
      activeTouches = e.touches.length
      captureBaseline(e)
      return
    }

    if (e.touches.length === 2) {
      e.preventDefault()
      const a = e.touches[0]
      const b = e.touches[1]
      const midX = (a.clientX + b.clientX) / 2
      const midY = (a.clientY + b.clientY) / 2

      // Scale is always derived from the gesture-start baseline (not accumulated
      // per-frame) which keeps the pinch stable and free of drift.
      const target = clamp(
        startScale * (touchDist(a, b) / startDist),
        MIN_ZOOM,
        MAX_ZOOM,
      )
      instance.zoomAbs(midX, midY, target)
      instance.moveBy(midX - lastMidX, midY - lastMidY, false)
      lastMidX = midX
      lastMidY = midY
    } else if (e.touches.length === 1) {
      const t = e.touches[0]
      // Only pan when zoomed in — at fit scale a one-finger swipe is a no-op,
      // so casual scrolling-style swipes never move or zoom the image.
      if (instance.getTransform().scale > 1) {
        e.preventDefault()
        instance.moveBy(t.clientX - lastPanX, t.clientY - lastPanY, false)
      }
      lastPanX = t.clientX
      lastPanY = t.clientY
    }
  }

  const onTouchEnd = (e: TouchEvent) => {
    if (!instance) return

    // Some fingers remain (e.g. pinch -> single-finger pan): re-baseline them.
    if (e.touches.length > 0) {
      activeTouches = e.touches.length
      captureBaseline(e)
      return
    }

    activeTouches = 0
  }

  const init = (container: HTMLElement) => {
    el = container
    // Register before panzoom() so our touchstart wins registration order.
    container.addEventListener("touchstart", onTouchStart, { passive: false })
    container.addEventListener("touchmove", onTouchMove, { passive: false })
    container.addEventListener("touchend", onTouchEnd)
    container.addEventListener("touchcancel", onTouchEnd)

    instance = panzoom(container, {
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      bounds: true,
      boundsPadding: 0,
      smoothScroll: false,
      // Disable panzoom's built-in double-click / double-tap zoom (desktop).
      zoomDoubleClickSpeed: 1,
    })
  }

  const dispose = () => {
    if (el) {
      el.removeEventListener("touchstart", onTouchStart)
      el.removeEventListener("touchmove", onTouchMove)
      el.removeEventListener("touchend", onTouchEnd)
      el.removeEventListener("touchcancel", onTouchEnd)
    }
    instance?.dispose()
    instance = null
    el = null
  }

  const zoomIn = () => {
    if (!instance) return
    const { scale } = instance.getTransform()
    instance.zoomAbs(
      window.innerWidth / 2,
      window.innerHeight / 2,
      Math.min(scale * ZOOM_STEP, MAX_ZOOM),
    )
  }

  const zoomOut = () => {
    if (!instance) return
    const { scale } = instance.getTransform()
    const newScale = Math.max(scale / ZOOM_STEP, MIN_ZOOM)
    instance.zoomAbs(window.innerWidth / 2, window.innerHeight / 2, newScale)
    if (newScale <= 1) instance.moveTo(0, 0)
  }

  const reset = () => {
    if (!instance) return
    instance.zoomAbs(0, 0, 1)
    instance.moveTo(0, 0)
  }

  // Keyboard pan — no-op at fit scale so arrow keys do nothing until zoomed in.
  // `bounds: true` (panzoom config) keeps the image from being panned off-screen.
  const panBy = (dx: number, dy: number) => {
    if (!instance) return
    if (instance.getTransform().scale <= 1) return
    instance.moveBy(dx, dy, true)
  }

  const resetAnimated = (durationMs = 300): Promise<void> => {
    return new Promise(resolve => {
      if (!instance || !el) {
        resolve()
        return
      }
      const { x, y, scale } = instance.getTransform()
      if (scale === 1 && x === 0 && y === 0) {
        resolve()
        return
      }
      el.style.transition = `transform ${durationMs}ms ease-in-out`
      instance.zoomAbs(0, 0, 1)
      instance.moveTo(0, 0)
      setTimeout(() => {
        if (el) el.style.transition = ""
        resolve()
      }, durationMs)
    })
  }

  const getTransform = (): Transform =>
    instance?.getTransform() ?? { x: 0, y: 0, scale: 1 }

  return {
    init,
    dispose,
    zoomIn,
    zoomOut,
    reset,
    resetAnimated,
    getTransform,
    panBy,
  }
}
